/**
 * Raising an invoice — the one form, used by both screens that raise one.
 *
 * Extracted rather than copied. /admin/billing and the Owner-only Invoices
 * page both mount this, and both post to the same endpoint, so there is no
 * second path for the tax figures, the validation or the client-rate snapshot
 * to disagree along. A copied form is the failure this file exists to prevent:
 * the two would drift the first time either was touched, and the figures they
 * disagreed about would be on invoices already sent to clients.
 *
 * Nothing here computes tax. billing_tax.py does that server-side, from rates
 * somebody supplied, and this form neither invents a rate nor recomputes a
 * figure it was given.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Lock, X } from 'lucide-react';
import { client as api } from '../api/client';
import { money } from '../lib/money';
import { formatPhone } from '../lib/phone';
import type { Client, FeeBasis, Page, Placement } from '../types';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function InvoiceForm({
  requirePlacement = false,
  title,
  onClose,
  snapshot,
  snapshotNote,
  initial,
  submitLabel = 'Create draft',
  submitting,
  figuresLocked = false,
  lockReason,
  payment,
  onSubmit,
}: {
  /**
   * Raised from the tracker view, which is the placement-billing view — so an
   * invoice raised there has to name the placement it bills for. The general
   * path keeps placement optional, because not everything billed is a
   * placement fee and that endpoint serves both.
   */
  requirePlacement?: boolean;
  /**
   * A heading and a close button, for the mounts that are popups.
   *
   * Both are optional because two of the four mounts are not popups at all —
   * /admin/billing and the blank form on this page render inline, below the
   * button that opened them, where there is nothing to close and the
   * surrounding page already says what the form is for.
   *
   * A popup needs both: an overlay with no title is a form that appeared from
   * nowhere, and one with no X is a form you can only leave by scrolling to
   * the bottom. The submit and cancel buttons down there still work; this is
   * the way out that does not require finding them first.
   */
  title?: string;
  onClose?: () => void;
  /**
   * The placement's own facts, shown but never editable.
   *
   * These are the fields the server copies onto the invoice when it is raised.
   * They are not inputs and must not become inputs: the point of snapshotting
   * is that the invoice records what was true at the moment it was raised
   * rather than whatever the candidate record says a year later. Shown here so
   * that raising is confirm-and-save rather than trust-and-save.
   */
  snapshot?: SnapshotFacts | null;
  /** Why that block cannot be edited, in words, for whoever wonders. */
  snapshotNote?: string;
  /**
   * Seed values, for editing an existing draft.
   *
   * The edit path mounts this same form rather than a second set of money
   * inputs, so the two cannot disagree about what an invoice is made of.
   * Absent when raising a new one, which starts empty.
   */
  initial?: Partial<{
    clientId: string;
    placementId: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    amount: string;
    taxAmount: string;
    notes: string;
  }>;
  submitLabel?: string;
  submitting: boolean;
  /**
   * The money, locked. Set once the invoice has been raised: the client holds
   * the figures and the server refuses to move them, so an editable box would
   * be a control that can only fail.
   */
  figuresLocked?: boolean;
  lockReason?: string;
  /**
   * Recording a receipt from this same form, so that editing an invoice and
   * recording money against it are not two separate places.
   *
   * `enabled` is false for a draft, where the section is shown with its reason
   * rather than hidden. A field that simply vanishes teaches nobody why, and
   * "raise it first" is the part worth knowing.
   */
  payment?: {
    /** What is still outstanding, for the over-payment check. */
    outstanding: string;
    /** The server refuses a receipt dated before this. */
    invoiceDate: string;
    enabled: boolean;
    disabledReason?: string;
  } | null;
  /**
   * The invoice body, and a payment body when one was filled in.
   *
   * Two arguments rather than one merged object, because they are two requests
   * to two endpoints under two different rules. Callers that only ever create
   * an invoice take the first and ignore the second.
   */
  onSubmit: (
    body: Record<string, unknown>,
    payment: Record<string, unknown> | null,
  ) => void;
}) {
  const [form, setForm] = useState({
    clientId: '',
    placementId: '',
    invoiceNumber: '',
    invoiceDate: today(),
    dueDate: '',
    amount: '',
    taxAmount: '0.00',
    notes: '',
    ...initial,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // Blank, not prefilled to the outstanding. A box already holding a number is
  // one somebody saves without reading, and that would record a receipt that
  // never arrived. RecordPaymentDialog prefills because recording is the only
  // thing it does; here it is one optional half of the form.
  const [received, setReceived] = useState('');
  const [tdsWithheld, setTdsWithheld] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);

  const settles = (Number(received) || 0) + (Number(tdsWithheld) || 0);
  const outstanding = Number(payment?.outstanding) || 0;
  // The rule the server applies, asked here to save a round trip to be
  // refused. Cash and withholding are checked together because together is how
  // they discharge the invoice.
  const overPaying = Boolean(payment?.enabled) && settles > outstanding + 0.005;
  const hasPayment = Boolean(payment?.enabled) && (Number(received) || 0) > 0;

  const clients = useQuery<Page<Client>>({
    queryKey: ['billing-clients'],
    queryFn: async () =>
      (await api.get('/api/v1/clients', { params: { limit: 200 } })).data,
  });

  // Only this client's placements: the API refuses an invoice pointing at
  // another client's placement, so offering them would just produce a 422.
  const placements = useQuery<Page<Placement>>({
    queryKey: ['billing-placements', form.clientId],
    enabled: Boolean(form.clientId),
    queryFn: async () =>
      (await api.get('/api/v1/placements', {
        params: { clientId: form.clientId, limit: 200 },
      })).data,
  });

  /**
   * A seeded client still gets its payment terms applied.
   *
   * applyClient does this when somebody picks a client by hand, but a form
   * opened from a placement never fires that: the client arrives in `initial`,
   * before the client list has loaded. Without this, the due date would be the
   * one field a pre-filled form still left blank.
   *
   * Once only, and never over a value already present -- the ref is what stops
   * it reapplying terms to a date somebody has just typed.
   */
  const seededTerms = useRef(false);
  const clientRows = clients.data?.items;
  useEffect(() => {
    if (seededTerms.current || !clientRows) return;
    seededTerms.current = true;
    setForm((f) => {
      if (!f.clientId || f.dueDate) return f;
      const chosen = clientRows.find((c) => c.id === f.clientId);
      if (!chosen || chosen.paymentTermsDays <= 0) return f;
      return {
        ...f,
        dueDate: new Date(
          new Date(f.invoiceDate).getTime() + chosen.paymentTermsDays * 86_400_000,
        )
          .toISOString()
          .slice(0, 10),
      };
    });
  }, [clientRows]);

  const selectedClient = clients.data?.items.find((c) => c.id === form.clientId);
  const selectedPlacement = placements.data?.items.find((p) => p.id === form.placementId);

  const previewTotal = (Number(form.amount) || 0) + (Number(form.taxAmount) || 0);

  /** Payment terms give a sensible due date; it stays editable. */
  const applyClient = (clientId: string) => {
    const chosen = clients.data?.items.find((c) => c.id === clientId);
    setForm((f) => ({
      ...f,
      clientId,
      placementId: '',
      dueDate:
        chosen && chosen.paymentTermsDays > 0
          ? new Date(
              new Date(f.invoiceDate).getTime()
                + chosen.paymentTermsDays * 86_400_000,
            )
              .toISOString()
              .slice(0, 10)
          : f.dueDate,
    }));
  };

  return (
    <form
      className="card space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (overPaying) return;
        onSubmit(
          {
            clientId: form.clientId,
            placementId: form.placementId || null,
            invoiceNumber: form.invoiceNumber.trim(),
            invoiceDate: form.invoiceDate,
            dueDate: form.dueDate || null,
            amount: form.amount,
            taxAmount: form.taxAmount || '0.00',
            notes: form.notes.trim() || null,
          },
          // Null unless money was actually entered. An empty box is not a
          // receipt of zero, and the endpoint refuses one anyway.
          hasPayment
            ? {
                amount: received,
                tdsWithheld: tdsWithheld.trim() || '0',
                receivedOn,
              }
            : null,
        );
      }}
    >
      {(title || onClose) && (
        <div className="-mt-1 flex items-start justify-between gap-3 border-b border-gray-200 pb-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {onClose && (
            <button
              type="button"
              className="btn-ghost p-1"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="i-client">Client</label>
          <select
            id="i-client"
            className="input"
            value={form.clientId}
            onChange={(e) => applyClient(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {clients.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="i-placement">
            Placement {requirePlacement ? '' : '(optional)'}
          </label>
          <select
            id="i-placement"
            className="input"
            value={form.placementId}
            onChange={(e) => set('placementId', e.target.value)}
            disabled={!form.clientId}
            required={requirePlacement}
          >
            <option value="">
              {requirePlacement ? 'Choose a placement…' : 'Not against a placement'}
            </option>
            {placements.data?.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.candidateName ?? p.id} · {p.position ?? 'role not recorded'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {snapshot && <Snapshot facts={snapshot} note={snapshotNote} />}

      {selectedPlacement && (
        <PlacementContext placement={selectedPlacement} onUseExpected={(v) => set('amount', v)} />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="label" htmlFor="i-number">Invoice Number</label>
          <input
            spellCheck={false}
            id="i-number"
            className="input font-mono"
            placeholder="HTS/25-26/P/003"
            value={form.invoiceNumber}
            onChange={(e) => set('invoiceNumber', e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Must be unique within this company.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label" htmlFor="i-date">Invoice Date</label>
            <input
              id="i-date"
              type="date"
              className="input"
              value={form.invoiceDate}
              onChange={(e) => set('invoiceDate', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="i-due">Due Date</label>
            <input
              id="i-due"
              type="date"
              className="input"
              value={form.dueDate}
              min={form.invoiceDate}
              onChange={(e) => set('dueDate', e.target.value)}
            />
            {selectedClient && selectedClient.paymentTermsDays > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {selectedClient.paymentTermsDays}-day terms
              </p>
            )}
          </div>
        </div>

        <div>
          <label className="label" htmlFor="i-amount">Amount (Before Tax)</label>
          <input
            spellCheck={false}
            id="i-amount"
            className="input font-mono"
            inputMode="decimal"
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            readOnly={figuresLocked}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="i-tax">Tax</label>
          <input
            spellCheck={false}
            id="i-tax"
            className="input font-mono"
            inputMode="decimal"
            value={form.taxAmount}
            onChange={(e) => set('taxAmount', e.target.value)}
            readOnly={figuresLocked}
          />
        </div>
      </div>

      {figuresLocked && lockReason && (
        <p className="flex items-start gap-1.5 rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {lockReason}
        </p>
      )}

      <p className="text-sm text-gray-600">
        Total <strong className="tabular-nums">{money(previewTotal.toFixed(2))}</strong>
        <span className="ml-2 text-xs text-gray-500">
          computed by the server from amount plus tax
        </span>
      </p>

      <div>
        <label className="label" htmlFor="i-notes">Notes</label>
        <textarea
          spellCheck={true}
          id="i-notes"
          className="input"
          rows={2}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      {payment && (
        <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900">Payment Received</h4>
            <span className="tabular-nums text-xs text-gray-500">
              {money(payment.outstanding)} outstanding
            </span>
          </div>

          {!payment.enabled ? (
            /* Shown and explained rather than hidden. record_payment refuses a
               draft outright, so live boxes here could only fail -- but
               removing them silently would leave somebody hunting for where
               payments went. */
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {payment.disabledReason}
            </p>
          ) : (
            <>
              <div className="mt-2 grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="label" htmlFor="i-received">Received Amount</label>
                  <input
                    spellCheck={false}
                    id="i-received"
                    className="input font-mono"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={received}
                    onChange={(e) => setReceived(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="i-tds-withheld">TDS Withheld</label>
                  <input
                    spellCheck={false}
                    id="i-tds-withheld"
                    className="input font-mono"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={tdsWithheld}
                    onChange={(e) => setTdsWithheld(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="i-received-on">Payment Date</label>
                  <input
                    id="i-received-on"
                    type="date"
                    className="input"
                    value={receivedOn}
                    min={payment.invoiceDate}
                    onChange={(e) => setReceivedOn(e.target.value)}
                  />
                </div>
              </div>

              <p className="mt-2 text-xs text-gray-500">
                TDS sits beside the amount rather than being deducted from it.
                Both count towards settling the invoice, so a receipt short by
                exactly the withholding still closes it. Leave the amount blank
                to save the invoice without recording a receipt.
              </p>

              {overPaying && (
                <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {money(settles.toFixed(2))} settles more than the{' '}
                  {money(payment.outstanding)} outstanding. Over-payment is
                  refused -- record what actually arrived, or correct the
                  invoice.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <button
        type="submit"
        className="btn-primary"
        disabled={submitting || overPaying}
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}

/**
 * What the placement says, beside what is being invoiced.
 *
 * Expected and invoiced are shown next to each other but never reconciled
 * automatically — they legitimately differ, and a guarantee period that has
 * not expired is a reason a human might bill less, or not at all.
 */
function PlacementContext({
  placement,
  onUseExpected,
}: {
  placement: Placement;
  onUseExpected: (value: string) => void;
}) {
  const guaranteeOpen =
    placement.guaranteeUntil !== null
    && new Date(placement.guaranteeUntil) >= new Date(today());

  return (
    <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-700">
          Expected from this placement{' '}
          <strong className="tabular-nums">{money(placement.billingAmount)}</strong>
        </p>
        {placement.billingAmount && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => onUseExpected(placement.billingAmount as string)}
          >
            Use this amount
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Status {placement.status}
        {placement.dateOfJoining && ` · joined ${placement.dateOfJoining}`}
      </p>

      {guaranteeOpen && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Replacement guarantee runs to {placement.guaranteeUntil}. A drop inside
          that window usually voids billing — this does not stop you invoicing,
          it is here so the decision is a deliberate one.
        </p>
      )}
    </div>
  );
}

/** The facts the invoice freezes when it is raised. */
export type SnapshotFacts = {
  candidateName: string | null;
  candidatePhone: string | null;
  position: string | null;
  dateOfJoining: string | null;
  location: string | null;
  ctcAnnual: string | null;
  recruiterName: string | null;
  feeBasis: FeeBasis | null;
  feePercent: string | null;
};

/**
 * The placement's facts, shown and not editable.
 *
 * Read-only because these are snapshot fields: the server copies them onto the
 * invoice at raise, and from then on the invoice keeps what was true then even
 * if the candidate record moves. Letting them be typed here would mean an
 * invoice could say something the placement never did.
 *
 * Rendered as text rather than as disabled inputs. A greyed box still looks
 * like somewhere to type, and reads as a field that is broken rather than one
 * that is decided elsewhere.
 */
function Snapshot({ facts, note }: { facts: SnapshotFacts; note?: string }) {
  const fee =
    facts.feeBasis === 'flat'
      ? 'Flat'
      : facts.feeBasis === 'ctc_percent'
        ? facts.feePercent
          ? facts.feePercent + '% of CTC'
          : '% of CTC'
        : null;

  const rows: [string, string | null][] = [
    ['Candidate', facts.candidateName],
    ['Mobile', formatPhone(facts.candidatePhone)],
    ['Position', facts.position],
    ['Date of joining', facts.dateOfJoining],
    ['Location', facts.location],
    ['CTC', facts.ctcAnnual ? money(facts.ctcAnnual) : null],
    ['Fee basis', fee],
    ['Recruiter', facts.recruiterName],
  ];

  return (
    <div className="rounded-xl bg-gray-50 p-3 ring-1 ring-gray-200">
      <div className="flex items-center gap-1.5">
        <Lock className="h-3.5 w-3.5 text-gray-400" />
        <h4 className="text-sm font-semibold text-gray-900">From the Placement</h4>
      </div>
      <dl className="mt-2 grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3 text-xs">
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-right text-gray-900">
              {value ?? <span className="text-gray-300">not recorded</span>}
            </dd>
          </div>
        ))}
      </dl>
      {note && <p className="mt-2 text-xs text-gray-500">{note}</p>}
    </div>
  );
}
